import React, { useEffect } from 'react';
import { 
  ArrowLeft, Phone, Mail, CheckCircle, Award, 
  Users, TrendingUp, Target, Building2
} from 'lucide-react';
import TrustedBy from './TrustedBy';

const BusinessPortal = ({ onBackToHome }) => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const pricingPackages = [
    {
      name: 'Basic Registration Package',
      price: 'N$ 1,495',
      description: 'The bare essentials to get you started',
      features: [
        'Name Reservation',
        'Founding Statement'
      ],
      popular: false,
      isNew: true
    },
    {
      name: 'Business Registration Standard',
      price: 'N$ 2,070',
      description: 'Perfect for new businesses getting started',
      features: [
        'Founding Statement',
        'Income Tax Certificate',
        'Consent Letter'
      ],
      popular: false
    },
    {
      name: 'Full Startup Package',
      price: 'N$ 3,150',
      description: 'Complete business setup solution',
      features: [
        'Founding Statement',
        'Income Tax Certificate',
        'Social Security Certificate',
        'SME Certificate',
        'Affirmative Action Certificate',
        'Consent Letter',
        'Good Standing Certificate'
      ],
      popular: true
    },
    {
      name: 'PTY Registration Standard',
      price: 'N$ 6,050',
      description: 'For private companies',
      features: [
        'Company Registration',
        'NamRA Tax Certificate',
        'Shareholder Certificate',
        'Free Consultation'
      ],
      popular: false
    },
    {
      name: 'PTY Registration Full Package',
      price: 'N$ 7,050',
      description: 'Complete PTY setup with all certificates',
      features: [
        'Founding Statement',
        'Income Tax Certificate',
        'Social Security Certificate',
        'Good Standing Certificate',
        'SME Certificate',
        'Affirmative Action Certificate',
        'Consent Letter',
        'Shareholder Certificate'
      ],
      popular: false
    }
  ];

  const services = [
    'New Business Start-up Company Registration',
    'Renewal and Updating of Company Documents',
    'Employee Education and Training Services',
    'Social Security Administration Services',
    'Business Growth & Expansion Analysis',
    'Tender Filling and Administration',
    'Business Plan Writing and Editing',
    'Maintenance of Statutory Records',
    'Lodging of Company Duty Fees',
    'Company Secretarial Services',
    'Drafting of Company Profiles',
    'Social Media Advertising',
    'Entrepreneurship Training',
    'Tax Administration Services',
    'Business Coaching'
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button 
                onClick={onBackToHome}
                className="flex items-center space-x-1 sm:space-x-2 text-black hover:text-blue-600 transition-colors group"
              >
                <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                <span className="font-medium hidden sm:inline">Back</span>
              </button>
              <div className="w-px h-6 bg-gray-300 hidden sm:block"></div>
              <div className="flex items-center space-x-2 sm:space-x-3">
                <img 
                  src="/business-icon-logo.jpeg" 
                  alt="Esh-Ham Business Consultants Logo" 
                  className="h-10 w-auto object-contain"
                />
                <div className="hidden sm:block">
                  <h1 className="text-lg font-bold text-black">Business Consultants</h1>
                  <p className="text-sm text-gray-600">Professional Business Services</p>
                </div>
              </div>
            </div>
            
            <nav className="hidden lg:flex items-center space-x-8">
              <a href="#services" className="text-black hover:text-blue-600 font-medium transition-colors">Services</a>
              <a href="#packages" className="text-black hover:text-blue-600 font-medium transition-colors">Packages</a>
              <a href="#contact" className="text-black hover:text-blue-600 font-medium transition-colors">Contact</a>
            </nav>
            
            <div className="flex items-center">
              <a href="tel:+26461306369" className="flex items-center space-x-1 sm:space-x-2 text-black hover:text-blue-600 transition-colors">
                <Phone className="w-4 h-4" />
                <span className="text-xs sm:text-sm font-medium hidden xs:inline">+264 61 306 369</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              <span className="text-black">Expert </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">Business Consulting</span>
            </h2>
            <p className="text-lg text-gray-600">in Namibia</p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <div className="flex items-center space-x-2 bg-gray-100 px-6 py-3 rounded-full">
                <Award className="w-5 h-5 text-blue-600" />
                <span className="text-base text-black font-semibold">13+ years in business</span>
              </div>
              <div className="flex items-center space-x-2 bg-gray-100 px-6 py-3 rounded-full">
                <Users className="w-5 h-5 text-blue-600" />
                <span className="text-base text-black font-semibold">10,000+ clients</span>
              </div>
              <div className="flex items-center space-x-2 bg-gray-100 px-6 py-3 rounded-full">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <span className="text-base text-black font-semibold">90% Success Rate</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Divider */}
      <div className="h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent my-4"></div>

      {/* Pricing Packages */}
      <section id="packages" className="py-10 sm:py-12 md:py-16 px-4 sm:px-6 lg:px-8 bg-gray-100">
        <div className="max-w-screen-2xl mx-auto">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-4">Our Packages</h3>
            <p className="text-lg text-blue-600">Choose the perfect package for your business needs</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {pricingPackages.map((pkg, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-2xl shadow-lg p-6 relative flex flex-col ${
                  pkg.popular ? 'border-4 border-blue-600' : pkg.isNew ? 'border-4 border-purple-600' : ''
                }`}>
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg whitespace-nowrap">
                      Most Popular
                    </span>
                  </div>
                )}
                {pkg.isNew && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-purple-600 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg whitespace-nowrap">
                      New
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6 flex-grow">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Building2 className="w-8 h-8 text-blue-600" />
                  </div>
                  <h4 className="text-xl font-bold text-black mb-2 min-h-[3.5rem]">{pkg.name}</h4>
                  <div className="text-4xl font-bold text-blue-600 mb-2">
                    {pkg.price}
                  </div>
                  <p className="text-gray-600 text-sm mb-6">{pkg.description}</p>
                  
                  <ul className="space-y-3 text-left mb-6">
                    {pkg.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-black text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <a href="#set-meeting-section" className={`w-full py-3 px-4 rounded-lg text-base font-semibold transition-all duration-300 transform hover:scale-105 mt-auto text-center ${
                  pkg.popular 
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg' 
                    : pkg.isNew 
                      ? 'bg-purple-600 text-white shadow-lg' 
                      : 'bg-gray-200 text-black hover:bg-gray-300'
                }`}>
                  Get Started
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Divider */}
      <div className="h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent my-4"></div>

      {/* Services Grid */}
      <section id="services" className="py-10 sm:py-12 md:py-16 bg-white relative overflow-hidden isolate">
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-purple-600 to-blue-600 opacity-15 rounded-full -translate-x-1/2 z-[-1] pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-4">Our Services</h3>
            <p className="text-lg text-blue-600">Comprehensive business solutions for your success</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="bg-gray-100 rounded-lg p-5 text-center group cursor-pointer hover:bg-gray-200 transition-colors"
              >
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <span className="text-base font-semibold text-black">{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="set-meeting-section" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gray-100 relative overflow-hidden isolate">
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-purple-600 to-blue-600 opacity-15 rounded-full translate-x-1/2 z-[-1] pointer-events-none"></div>
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="mb-8">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-12 h-12 text-blue-600" />
              </div>
              <h3 className="text-4xl lg:text-5xl font-bold text-black mb-4">
                Ready to Experience Best-in-Class Business Support?
              </h3>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Whatever your business needs—big or small—we deliver professional results with local expertise and international standards.
              </p>
            </div>
            
            <a href="https://wa.me/264814247723" target="_blank" rel="noopener noreferrer" className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold py-5 px-10 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 shadow-xl">
              Set up a meeting with our founder
            </a>
            
            <div className="mt-8 pt-8 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-gray-600">
                <a href="tel:+26461306369" className="flex items-center space-x-2 hover:text-blue-600 transition-colors">
                  <Phone className="w-5 h-5 text-blue-600" />
                  <span className="text-base font-medium">+264 61 306 369</span>
                </a>
                <div className="hidden sm:block w-px h-6 bg-gray-300"></div>
                <a href="mailto:info@eshham.com" className="flex items-center space-x-2 hover:text-blue-600 transition-colors">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <span className="text-base font-medium">info@eshham.com</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted By */}
      <TrustedBy />

      {/* Contact Section */}
      <section id="contact" className="py-10 sm:py-12 md:py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
            <div>
              <h4 className="font-semibold mb-4 text-xl flex items-center">
                <Phone className="w-5 h-5 text-blue-600 mr-2" />
                Contact Us
              </h4>
              <div className="space-y-3">
                <a href="tel:+26461306369" className="flex items-center space-x-3 hover:text-blue-600 transition-colors">
                  <span className="text-base">+264 61 306 369</span>
                </a>
                <a href="mailto:info@eshham.com" className="flex items-center space-x-3 hover:text-blue-600 transition-colors">
                  <span className="text-base">info@eshham.com</span>
                </a>
                <div className="mt-4 text-gray-400 text-base">
                  <p>Southern Industrial Area</p>
                  <p>Armstrong Street, Old Power Station</p>
                  <p>Windhoek, Namibia</p>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4 text-xl">Business Hours</h4>
              <div className="space-y-2 text-gray-400 text-base">
                <p>Monday - Friday: 8:00 AM - 5:00 PM</p>
                <p>Saturday: 9:00 AM - 1:00 PM</p>
                <p>Sunday: Closed</p>
              </div>
            </div>
            
            <div className="sm:col-span-2 md:col-span-1">
              <h4 className="font-semibold mb-4 text-xl">Get Started Today</h4>
              <p className="text-gray-400 mb-4 text-base">
                Ready to start your business journey? Contact us for a free consultation.
              </p>
              <a href="https://wa.me/264814247723" target="_blank" rel="noopener noreferrer" className="inline-block text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold py-3 px-6 rounded-lg text-base transition-all duration-300 transform hover:scale-105 shadow-lg">
                Schedule Consultation
              </a>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
            <p>&copy; 2025 Esh-Ham Business Consultants. All rights reserved. | CC/2011/1182</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BusinessPortal;
